import {BaseStore, globalStore} from "../../stem-core/state/Store";
import {SubscriptionOffer} from "./SubscriptionOfferStore";
import {field} from "../../stem-core/state/StoreField";
import {apiClient, type MerchantScopedRequest} from "../connection/BlinkApiClient";
import type {StoreId} from "../../stem-core/state/State";
import {BaseEnum, makeEnum} from "../../stem-core/state/BaseEnum";
import {Merchant} from "./MerchantStore";
import {StemDate} from "../../stem-core/time/Date";

@makeEnum
export class CoverageChangeType extends BaseEnum {
    static UPGRADE;
    static DOWNGRADE;
    static EQUIVALENT;
    static OTHER;

    // For downgrades there's no reason to apply the conversion now, since you're not getting any money back
    canAllowInstantConversion(): boolean {
        return this === CoverageChangeType.UPGRADE || this === CoverageChangeType.OTHER;
    }
}


@globalStore
export class SubscriptionCoverage extends BaseStore("SubscriptionCoverage") {
    // Set by MerchantSubscriptionsPage from the offers summary, not sent by the server
    declare currentSubscribersCount?: number;
    declare allTimeSubscribersCount?: number;

    @field("Merchant") merchant: Merchant;
    @field(String) name: string;
    @field(Date) deletedAt: StemDate;
    @field(Object) description: object[]; // An array of description items
    @field(Boolean) requiresAddress: boolean;
    @field(Boolean) addressRequiresPostalCode: boolean;
    @field(Boolean) addressRequiresPhoneNumber: boolean;
    @field(Boolean) billableProRata: boolean;
    @field(String) blinkCoverage: string;
    @field(String) extraCoverage: string;
    @field("SubscriptionCoverage") includesCoverage: SubscriptionCoverage;
    @field(Array) benefitIds: number[];

    getName(): string {
        return this.name;
    }

    // Also the benefits of the coverages this one includes, transitively, like get_full_benefit_ids on the backend
    getFullBenefitIds(): number[] {
        const benefitIds = new Set<number>();
        const visited = new Set<SubscriptionCoverage>();
        let coverage: SubscriptionCoverage | null = this;
        // Break a cycle rather than loop on it
        while (coverage && !visited.has(coverage)) {
            visited.add(coverage);
            for (const benefitId of coverage.benefitIds) {
                benefitIds.add(benefitId);
            }
            coverage = coverage.includesCoverage;
        }
        return Array.from(benefitIds);
    }

    getDescription(): any[] {
        return this.description;
    }

    getAvailableOffers(): SubscriptionOffer[] {
        return SubscriptionOffer.filter(offer => offer.coverageId === this.id && offer.isAvailable());
    }

    // TODO @branch rename to isActive?
    isAvailable(): boolean {
        return !this.deletedAt && this.getAvailableOffers().length > 0;
    }

    toString(): string {
        return this.getName();
    }

    toUI(): string {
        return this.toString();
    }
}

// CreateSubscriptionCoverage (api/merchants/views.py)
export interface CreateSubscriptionCoverageRequest extends MerchantScopedRequest {
    name: string;
    description: Record<string, any>;
    requiresAddress: boolean;
    billableProRata: boolean;
    blinkCoverage?: string;
    extraCoverage?: string;
}

export async function apiMerchantCreateSubscriptionCoverage(request: CreateSubscriptionCoverageRequest): Promise<SubscriptionCoverage> {
    const response = await apiClient.post("/merchant/create_subscription_coverage", request);
    return SubscriptionCoverage.loadObject(response);
}

// EditSubscriptionCoverageRequest. Nullable throughout, since the call passes allowNulls
export interface EditSubscriptionCoverageRequest extends MerchantScopedRequest {
    coverageId: StoreId;
    name?: string | null;
    description?: Record<string, any> | null;
    blinkCoverage?: string | null;
    extraCoverage?: string | null;
    billableProRata?: boolean | null;
    requiresAddress?: boolean | null;
    addressRequiresPostalCode?: boolean | null;
    addressRequiresPhoneNumber?: boolean | null;
    includesCoverageId?: StoreId | null;
}

export async function apiMerchantEditSubscriptionCoverage(request: EditSubscriptionCoverageRequest): Promise<any> {
    return apiClient.post("/merchant/edit_subscription_coverage", request, {allowNulls: true});
}
